// 3rd-party modules
import Tooltip from "antd/lib/tooltip";
import { useEffect, useState } from "react";

// project modules
import AccountKiboshDeviceMenuModal from "../kibosh-device/AccountKiboshDeviceMenuModal";
import AccountVpnDevicePickerModal from "./AccountVpnDevicePickerModal";
import Button from "../../shared/button";
import ListView from "../../shared/list/listView";
import Loader from "../../shared/loader";
import { apiCall } from "../../../helpers/apiHelper";
import { ListViewType } from "../../shared/list/useListHook";
import { Text } from "../../shared/text";

// apis
import * as AccountKiboshDeviceApi from '../../../apis/accountKiboshDeviceApi';
import * as CommonValueApi from '../../../apis/commonValueApi';
import * as DashApi from '../../../apis/dashApi';

// models
import { Account } from "../../../models/account";
import { AccountKiboshVpnDevice } from "../../../models/accountKiboshVpnDevice";
import { AccountKiboshDevice } from "../../../models/accountKiboshDevice";
import { DataSourceRequest } from "../../../models/dataSourceRequest";
import { CommonValue } from "../../../models/commonValue";
import { AccountDeviceGroup } from "../../../models/accountDeviceGroup";
import { useNavigate } from "react-router-dom";

type Props = {
  account?: Account;
  accountKiboshDevice?: AccountKiboshDevice;
  standalone?: boolean;
  refresh?: boolean;
  accountDeviceGroup?: AccountDeviceGroup;
  accountDeviceGroupVpnsIds?: any[];
  onCancel?: () => void;
  onMembersListChange?: () => void;
  onSave?: (account: Account) => void;
};

export default function AccountVpns({ account, refresh, standalone = false, accountKiboshDevice, accountDeviceGroup, accountDeviceGroupVpnsIds = [], onCancel, onSave, onMembersListChange }: Props) {
  const [accountKiboshVpnDevices, setAccountKiboshVpnDevices] = useState<AccountKiboshVpnDevice[]>([]);
  const [currentAccountKiboshVpnDevice, setCurrentAccountKiboshVpnDevice] = useState<AccountKiboshVpnDevice>(new AccountKiboshVpnDevice());
  const [vpnAssignedDevicesIds, setVpnAssignedDevicesIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDevicesTypeList, setLoadingDevicesTypeList] = useState(false);
  const [loadingAccountKiboshDevices, setLoadingAccountKiboshDevices] = useState(false);
  const [reload, setReload] = useState<boolean>(false);
  const [showAccountKiboshVpnDeviceModal, setShowAccountKiboshVpnDeviceModal] = useState(false);
  const [showAccountKiboshVpnDeviceMenuModal, setShowAccountKiboshVpnDeviceMenuModal] = useState(false);

  const navigate = useNavigate();
  let abortController = new AbortController();

  useEffect(() => {
    let abortController = new AbortController();

    setReload(true);

    return () => { abortController.abort(); }
  }, []);

  useEffect(() => {
    if (accountKiboshDevice?.accountKiboshDeviceId) {
      setReload(true);
    }
  }, [accountKiboshDevice]);

  useEffect(() => {
    if (reload) {
      getAccountKiboshVpnDevicesAsync();
      setReload(false);
    }
  }, [reload]);

  const getAccountKiboshVpnDevicesAsync = async () => {
    if (accountKiboshDevice?.accountKiboshDeviceId) {
      Promise.all([
        getAccountKiboshVpnDevices({}, abortController.signal),
        getDeviceTypes(),
        getAccountKiboshDevices({}),
      ]).then(
        (results: any) => {
          let vpnDevices = results[0];
          const deviceVpnType = results[1]?.filter((x: any) => x.value === "vpn");
          const kiboshDevices = results[2] ?? [];

          if (kiboshDevices.length) {
            vpnDevices = vpnDevices.map((x: any) => {
              const currentDevice = kiboshDevices.find((y: any) => y.kiboshDeviceReference === x.id);
              return {
                ...x,
                isStandalone: currentDevice?.kiboshDeviceReference ? currentDevice.isStandalone : false,
                hasActiveSubscription: currentDevice?.hasActiveSubscription || false
              };
            });
          }


          if (deviceVpnType.length) {
            vpnDevices = vpnDevices.map((x: any)=> {
              return {
                ...x,
                kiboshDeviceTypeId: deviceVpnType[0].commonValueId,
                kiboshDeviceTypeCode: deviceVpnType[0].value,
                kiboshDeviceType: deviceVpnType[0].value,
                kiboshDeviceReference: x.id,
                vpnType: x.vpnType,
                accountKiboshDeviceId: x.id,
                vpnId: x.vpnId || x.id
              }
            })
          }

          setVpnAssignedDevicesIds(vpnDevices.filter((x: any) => x.associatedRouterClientId).map((x: any) => x.associatedRouterClientId));

          if (!!accountDeviceGroup?.id) {
            vpnDevices = vpnDevices.filter((x: any) => accountDeviceGroupVpnsIds.includes(x.vpnId));
          } else if (standalone) {
            // sort vpns to show them to user ordered by the ids
            vpnDevices = vpnDevices.sort((a: any, b: any) => {
              const getParts = (data: string) =>
                data
                  .replace(/^v-/, '') // Remove "v-" prefix
                  .split('-')         // Split by "-"
                  .map(Number);       // Convert each part to number

              const aParts = getParts(a.kiboshDeviceReference);
              const bParts = getParts(b.kiboshDeviceReference);

              const length = Math.max(aParts.length, bParts.length);

              for (let i = 0; i < length; i++) {
                const aVal = aParts[i] ?? 0;
                const bVal = bParts[i] ?? 0;

                if (aVal !== bVal) {
                  return aVal - bVal;
                }
              }

              return 0; // All parts are equal
            });
            vpnDevices = vpnDevices.filter((x: any) => x.isStandalone && x.hasActiveSubscription);
          }

          vpnDevices = vpnDevices.map((x: any, index: any)=> {
            return {
              ...x,
              kiboshDeviceName: `VPN ${index + 1}`
            }
          })

          setAccountKiboshVpnDevices(vpnDevices);
        }
      );
    } else {
      setAccountKiboshVpnDevices(await getAccountKiboshVpnDevices({}, abortController.signal))
    }
  }

  const getAccountKiboshVpnDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoading(true);
    const response = await apiCall(DashApi.getAccountVpns(null, accountKiboshDevice?.kiboshDeviceReference, abortController.signal));
    setLoading(false);

    return response.success ? AccountKiboshVpnDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const getDeviceTypes = async (abortSignal?: AbortSignal) => {
    setLoadingDevicesTypeList(true);
    const response = await apiCall(CommonValueApi.getDeviceTypes(abortSignal));
    setLoadingDevicesTypeList(false);

    return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
  }

  const getAccountKiboshDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoadingAccountKiboshDevices(true);
    const response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(account?.accountId!, request, abortSignal));
    setLoadingAccountKiboshDevices(false);

    return response.success ? AccountKiboshDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const onAssignToDeviceClick = async (accountKiboshVpnDevice: AccountKiboshVpnDevice, event?: any) => {
    if (event) {
      event.stopPropagation();
    }
    setCurrentAccountKiboshVpnDevice({ ...accountKiboshVpnDevice });
    setShowAccountKiboshVpnDeviceModal(true)
  }

  const onDetailsClick = (accountKiboshVpnDevice: AccountKiboshVpnDevice, event?: any) => {
    if (event) {
      event.stopPropagation();
    }
    setCurrentAccountKiboshVpnDevice({ ...accountKiboshVpnDevice, kiboshDeviceReference: accountKiboshDevice?.kiboshDeviceReference });
    setShowAccountKiboshVpnDeviceMenuModal(true);
  };

  const onCreateNewVpnClick = () => {
    navigate("/portal/create-new-subscription?mode=VPN");
  }

  const BlockItem: React.FC<{ data: any }> = ({ data }) => (
    <group
      data-test=""
      data-justify="space-between"
      data-direction="column"
      data-contain=""
      data-border=""
      data-radius="10"
      data-background="context"
      data-cursor="pointer">
      <group data-gap="5" data-width="auto" data-align="center" data-justify="start" data-space="5">
        <icon data-color={!data.associatedRouterClientId ? "" : "main"} data-opacity={!data.associatedRouterClientId ? "50" : undefined} data-icon-size="48">vpn_lock</icon>
      </group>
      <group data-direction="column" data-contain="" data-gap="5" data-space="15">
        <Text
          data-weight="700"
          data-ellipsis=""
          data-text-align="center">
          {data.kiboshDeviceName || data.vpnId}
        </Text>
        <Text data-wrap="wrap" data-clamp="3" data-line="1.5" data->
          {data.name || ( !data.vpnId ? "Available +" : "")}
        </Text>
      </group>
        <group data-gap="5" data-space="10" data-align="center" data-justify="end" >
          <div className="button micro" data-color={!!data.vpnIsConnected ? "main-dark" : "red"}>
            <icon className={!!data.vpnIsConnected ? "text-shadow" : ""}>power_settings_new</icon>
          </div>
          <Button micro material icon="download" onClick={(e) => onAssignToDeviceClick(data, e)} />
          <separator data-vertical=""></separator>
          <Button micro material icon="arrow_outward" disabled={(data.isStandalone && !data.initialized) || (!data.isStandalone && !data.associatedRouterClientId)} onClick={(e) => onDetailsClick(data, e)} />
        </group>
    </group>
  );

  const GridItem: React.FC<{ data: any }> = ({ data }) => (
    <group data-align="center" data-space="10" data-gap="10" data-wrap="no" data-background="highlight" >
      <group data-gap="5" data-width="auto" data-align="center" data-justify="start" data-space="5">
        <icon data-color={!data.associatedRouterClientId ? "" : "main"} data-opacity={!data.associatedRouterClientId ? "50" : undefined} data-icon-size="48">vpn_lock</icon>
      </group>
      <group data-direction="column" data-contain="">
        <Text data-weight="700">{data.kiboshDeviceName || data.vpnId}</Text>
        <Text data-ellipsis="">{data.name || ( !data.vpnId ? "Available +" : "")}</Text>
      </group>
        <group
          data-align="center"
          data-wrap="no"
          data-position="right"
          data-width="auto"
        >
          <div className="button mini" data-color={!!data.vpnIsConnected ? "main-dark" : "red"}>
            <icon className={!!data.vpnIsConnected ? "text-shadow" : ""}>power_settings_new</icon>
          </div>
          <Button mini rounded onClick={(e) => onAssignToDeviceClick(data, e)}>
            <icon>download</icon>
          </Button>
          <separator data-vertical=""></separator>
          <Button mini rounded disabled={(data.isStandalone && !data.initialized) || (!data.isStandalone && !data.associatedRouterClientId)} onClick={(e) => onDetailsClick(data, e)}>
            <icon>more_vert</icon>
          </Button>
        </group>
    </group>

  );

  return (
    <>
      {(loading || loadingDevicesTypeList || loadingAccountKiboshDevices) && <Loader />}
      <view data-vertical="">
        <view
          data-test=""
          data-scroll=""
          data-space="20"
          data-gap="30">
          <group data-gap="10" data-direction="column">
            <text data-weight="700" data-text-size="xx-large" data-wrap="wrap" data-color="main-dark">{!!accountDeviceGroup?.id && `${accountDeviceGroup?.name} `}Mobile</text>
            <text data-wrap="wrap" data-length="400" data-line="1.5" data-light="">Secure your mobile devices on the go with comprehensive VPN management. Here, you can configure all aspects of your VPN setup, including adding, removing, modifying, and managing access settings.</text>
            <group><a style={{ color: "#0375ba"}} target="_blank" href="https://kibosh.com/docs-category/home">Help</a></group>
          </group>
          <group data-width="auto" data-position="left" data-align="center" data-gap="10">
            <Tooltip title="Refresh" arrow={false}>
              <group data-width="auto">
                <Button
                  secondary
                  data-length="50"
                  data-height="50"
                  data-elevation="6"
                  rounded
                  icon="refresh"
                  disabled={!accountKiboshDevice?.kiboshDeviceReference}
                  onClick={() => setReload(true)}
                />
              </group>
            </Tooltip>
            {
              <>
                <separator data-vertical=""></separator>
                <Button
                  material
                  text="Add VPN"
                  icon="add"
                  outline
                  large
                  onClick={onCreateNewVpnClick}/>
              </>
            }
          </group>
          <ListView
            data-adaptive="desktop"
            dataSource={accountKiboshVpnDevices}
            view={ListViewType.Block}
            keyField="id"
            data-template="190"
            listProps={{ "data-gap": "10" }}
            itemComponent={BlockItem}
            onItemClick={(data) => onAssignToDeviceClick(data)}/>
          <group data-adaptive="mobile" data-margin-bottom="100">
            <group data-contain="" data-radius="10" data-elevation="2">
              <ListView
                view={ListViewType.List}
                dataSource={accountKiboshVpnDevices}
                keyField="id"
                data-space="0"
                listProps={{ "data-gap": "1" }}
                itemComponent={GridItem}
                onItemClick={(data) => onAssignToDeviceClick(data)}/>
            </group>
          </group>
        </view>
      </view>
      {!!showAccountKiboshVpnDeviceMenuModal && (
        <AccountKiboshDeviceMenuModal
          open={showAccountKiboshVpnDeviceMenuModal}
          closeOnSave={true}
          account={account!}
          accountKiboshDevice={currentAccountKiboshVpnDevice}
          deviceReference={currentAccountKiboshVpnDevice.associatedRouterClientId}
          onClose={() => setShowAccountKiboshVpnDeviceMenuModal(false)}
        />
      )}
      {!!showAccountKiboshVpnDeviceModal &&
        <AccountVpnDevicePickerModal
          open={showAccountKiboshVpnDeviceModal}
          account={account!}
          accountKiboshDevice={accountKiboshDevice!}
          accountKiboshVpnDevice={currentAccountKiboshVpnDevice}
          vpnAssignedDevicesIds={vpnAssignedDevicesIds}
          onClose={() => setShowAccountKiboshVpnDeviceModal(false)}
          onSave={() => setReload(true)} />
      }
    </>
  );
}
